import React, { useState } from 'react';
import axios from 'axios';
import { Mail, Clock, Truck, Send, Phone } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { useToast } from '../hooks/use-toast';
import { Reveal } from '../components/shared';
import { PageHeader } from './Flooring';
import { business, faqs } from '../mock/mock';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';

export default function Contact() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast({ title: 'Message sent!', description: 'Thanks for reaching out — we’ll be in touch soon.' });
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      toast({ title: 'Something went wrong', description: 'Please try again or call us directly.', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <PageHeader
        eyebrow="Contact"
        title="Let’s talk about your project"
        sub="Questions, quotes, or scheduling — we’re here to help. Reach out and a real person will get back to you."
        image="https://images.unsplash.com/photo-1560184897-1ee3713708ee?auto=format&fit=crop&w=1920&q=80"
      />

      <section className="max-w-7xl mx-auto px-6 py-16 md:py-20 grid lg:grid-cols-2 gap-12">
        {/* info */}
        <div className="space-y-6">
          <Reveal>
            <div>
              <h2 className="font-display text-3xl font-semibold text-brand-charcoal">Get in touch</h2>
              <p className="text-brand-stone mt-3 text-[17px] leading-relaxed">Call us or send a message and a real person will get back to you — usually within one business day.</p>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 gap-4">
            <Reveal delay={60}>
              <a href={business.phoneHref} className="block bg-white rounded-2xl border border-brand-line shadow-sm p-6 hover:shadow-md transition-shadow h-full">
                <Phone size={22} className="text-brand-clay" />
                <p className="mt-3 text-sm text-brand-stone">Call or text</p>
                <p className="font-semibold text-brand-charcoal text-lg">{business.phone}</p>
              </a>
            </Reveal>
            <Reveal delay={120}>
              <a href={`mailto:${business.email}`} className="block bg-white rounded-2xl border border-brand-line shadow-sm p-6 hover:shadow-md transition-shadow h-full">
                <Mail size={22} className="text-brand-clay" />
                <p className="mt-3 text-sm text-brand-stone">Email us</p>
                <p className="font-semibold text-brand-charcoal text-lg break-all">{business.email}</p>
              </a>
            </Reveal>
            <Reveal delay={60}>
              <div className="bg-white rounded-2xl border border-brand-line shadow-sm p-6 h-full">
                <Truck size={22} className="text-brand-clay" />
                <p className="mt-3 text-sm text-brand-stone">Service area</p>
                <p className="font-semibold text-brand-charcoal">{business.serviceArea}</p>
              </div>
            </Reveal>
            <Reveal delay={120}>
              <div className="bg-white rounded-2xl border border-brand-line shadow-sm p-6 h-full">
                <Clock size={22} className="text-brand-clay" />
                <p className="mt-3 text-sm text-brand-stone">Hours</p>
                <p className="font-semibold text-brand-charcoal">Mon–Fri 9–6</p>
                <p className="text-sm text-brand-stone">Sat 10–4 · Sun by appt.</p>
              </div>
            </Reveal>
          </div>
        </div>

        {/* form */}
        <Reveal delay={120}>
          <form onSubmit={submit} className="bg-white rounded-2xl border border-brand-line shadow-sm p-7 md:p-9 space-y-5">
            <h2 className="font-display text-2xl font-semibold text-brand-charcoal">Send us a message</h2>
            <div className="space-y-2">
              <Label htmlFor="cname">Name *</Label>
              <Input id="cname" required value={form.name} onChange={(e) => update('name', e.target.value)} placeholder="Your name" className="h-12" />
            </div>
            <div className="grid sm:grid-cols-2 gap-5">
              <div className="space-y-2">
                <Label htmlFor="cemail">Email *</Label>
                <Input id="cemail" type="email" required value={form.email} onChange={(e) => update('email', e.target.value)} placeholder="you@email.com" className="h-12" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="cphone">Phone</Label>
                <Input id="cphone" value={form.phone} onChange={(e) => update('phone', e.target.value)} placeholder="(571) 555-0123" className="h-12" />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="cmessage">Message *</Label>
              <Textarea id="cmessage" required rows={5} value={form.message} onChange={(e) => update('message', e.target.value)} placeholder="How can we help?" />
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-brand-clay hover:bg-brand-clayDark text-white rounded-full h-14 text-base font-medium">{loading ? 'Sending…' : (<>Send Message <Send size={17} className="ml-1" /></>)}</Button>
          </form>
        </Reveal>
      </section>

      {/* FAQ */}
      <section className="bg-brand-cream">
        <div className="max-w-3xl mx-auto px-6 py-20">
          <Reveal>
            <div className="text-center">
              <span className="text-brand-clay text-xs font-semibold uppercase tracking-[0.25em]">FAQ</span>
              <h2 className="font-display text-3xl md:text-4xl font-semibold text-brand-charcoal mt-3">Common questions</h2>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <Accordion type="single" collapsible className="mt-10 space-y-3">
              {faqs.map((f, idx) => (
                <AccordionItem key={idx} value={`item-${idx}`} className="bg-white rounded-xl border border-brand-line px-5">
                  <AccordionTrigger className="text-left font-medium text-brand-charcoal hover:no-underline py-5">{f.q}</AccordionTrigger>
                  <AccordionContent className="text-brand-stone text-[15px] leading-relaxed pb-5">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>
    </>
  );
}
